import {
  IMessageRecipient,
  RecipientTypeEnum,
} from 'Modules/Communication/shared/models/Inbox'
import { MessageTypeEnum } from 'Modules/Communication/shared/models/Recipients'
import { ChangeEvent, createRef, MouseEvent, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'shared/store/store'
import {
  SEND_EMAIL_REMOVE_RECIPIENT_REQUESTED,
  SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED,
  SEND_EMAIL_UPDATE_RECIPIENTS_REQUESTED,
} from 'shared/store/sendMessage/email/actionTypes'
import { getSendEmailRecipients } from 'shared/store/sendMessage/email/selectors'
import { handleRecipientModal } from 'shared/store/sendMessage/recipients'
import { getFrequentRecipients } from 'shared/store/sendMessage/recipients/selectors'
import {
  SEND_SMS_REMOVE_RECIPIENT_REQUESTED,
  SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED,
  SEND_SMS_UPDATE_RECIPIENTS_REQUESTED,
} from 'shared/store/sendMessage/sms/actionTypes'
import { getSendSmsRecipients } from 'shared/store/sendMessage/sms/selectors'
import { EmailValidation } from 'shared/utils/validations'
import {
  checkIfDuplicatePhonenumber,
  removeAllSpaces,
} from '../../shared/utils/handle-recipients-helper'
import { isValidMobileNumber } from '../../shared/utils/send-sms-helper'

const useRecipientHandle = (
  recipientType: RecipientTypeEnum,
  messageType: MessageTypeEnum
) => {
  const dispatch = useAppDispatch()
  const ref = createRef<HTMLDivElement>()
  const frequentRecipients = useAppSelector(getFrequentRecipients)
  const smsMessageRecipients = useAppSelector(getSendSmsRecipients)
  const emailMessageRecipients = useAppSelector(getSendEmailRecipients)

  const isEmail = messageType === MessageTypeEnum.Email
  const isSMS = messageType === MessageTypeEnum.SMS

  const [error, setError] = useState<string>('')
  const [term, setTerm] = useState<string>('')

  const _isDuplicateSmsInSendMessage = (str: string) => {
    return smsMessageRecipients.some((rec) => {
      if (!rec.phonenumber) return false
      return checkIfDuplicatePhonenumber(rec.phonenumber, str)
    })
  }

  const _isDuplicateEmailInSendMessage = (str: string) => {
    return emailMessageRecipients.some((rec) => {
      if (!rec.email) return false
      return rec.email === str
    })
  }

  const _checkIfExistsInFrequentRecipients = (
    match: string
  ): IMessageRecipient | null => {
    const frequent = isEmail
      ? frequentRecipients.email.data
      : frequentRecipients.sms.data

    const existing = frequent?.find((recipient) => {
      if (isEmail) {
        return recipient.email === match
      }

      return checkIfDuplicatePhonenumber(match, recipient.phonenumber)
    })

    if (!existing) return null

    return {
      nameregisterId: existing.nameId,
      email: isEmail ? match : existing.email,
      recipientType,
      name: existing.name,
      phonenumber: isEmail ? existing.phonenumber : match,
    }
  }

  const _checkIfRelatedTarget = (e: ChangeEvent<HTMLInputElement>) => {
    const event = e as unknown as MouseEvent<HTMLInputElement>
    const eventTarget = event.relatedTarget as HTMLElement | null
    return ref.current?.contains(eventTarget) || event.target === ref.current
  }

  const checkIfMatchExists = (match: string) => {
    if (isSMS) {
      return _isDuplicateSmsInSendMessage(match)
    }

    if (isEmail) {
      return _isDuplicateEmailInSendMessage(match)
    }

    return false
  }

  const resetTerm = () => {
    setError('')
    setTerm('')
  }

  const handleSelect = async (
    e: { [x: string]: any },
    relatedTarget?: boolean
  ) => {
    setError('')

    const value = e.value as string

    const property = isEmail ? 'email' : 'phonenumber'

    const containsComma = value.includes(',')
    const containSpace = value.includes(' ')
    // Multiple recipients
    if (containsComma || containSpace) {
      let inputs: string[] = []

      if (isEmail) {
        // separate by , or space
        inputs = value
          .split(/,| /)
          .filter(Boolean)
          .map((match) => match.trim())
      }

      if (isSMS) {
        // Only separate by , and remove all places
        inputs = value
          .split(',')
          .filter(Boolean)
          .map((match) => removeAllSpaces(match.trim()))
      }

      // validate each email or phonenumber
      const recipients = await Promise.all(
        inputs.map(async (match) => {
          try {
            const valid = isEmail
              ? await EmailValidation.isValid(match)
              : await isValidMobileNumber(match)

            if (!valid) return null

            const hasMatch = checkIfMatchExists(match)
            if (hasMatch) {
              return null
            }

            const frequentRecipient = _checkIfExistsInFrequentRecipients(match)
            if (frequentRecipient) {
              return frequentRecipient
            }

            return {
              [property]: match,
              recipientType,
            } as Partial<IMessageRecipient>
          } catch {
            return null
          }
        })
      )

      const validRecipients = recipients.filter(Boolean) as IMessageRecipient[]

      // Remove duplicates from current pasted string
      const reducedRecipients = validRecipients.reduce(
        (acc: IMessageRecipient[], curr: IMessageRecipient) => {
          const existing = acc.find((recipient) => {
            if (isEmail) {
              return recipient.email === curr?.email
            }

            if (!curr?.phonenumber || !recipient.phonenumber) {
              return false
            }

            return checkIfDuplicatePhonenumber(
              curr.phonenumber,
              recipient.phonenumber
            )
          })

          if (existing) return acc
          return [...acc, curr]
        },
        [] as IMessageRecipient[]
      )

      const type = isEmail
        ? SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED
        : SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED

      if (reducedRecipients.length > 0) {
        dispatch({
          type,
          payload: reducedRecipients,
        })

        setTerm('')
        return
      }
    } else {
      // Single recipients
      const isValid = isEmail
        ? EmailValidation.isValidSync(e.value)
        : isValidMobileNumber(e.value)

      if (isValid) {
        // Just check so we dont have duplicates in recipients
        const hasMatch = checkIfMatchExists(e.value)
        if (hasMatch) {
          return null
        }

        const recipient =
          _checkIfExistsInFrequentRecipients(e.value) ||
          ({
            [property]: e.value,
            recipientType,
          } as Partial<IMessageRecipient>)

        const type = isEmail
          ? SEND_EMAIL_UPDATE_RECIPIENTS_REQUESTED
          : SEND_SMS_UPDATE_RECIPIENTS_REQUESTED

        dispatch({
          type,
          payload: recipient,
        })

        setTerm('')
        return
      }
    }

    if (relatedTarget) return

    if (term.length > 0) {
      const message = isEmail
        ? 'Ugyldig e-post'
        : 'Ugyldig telefonnummer, kun norske mobilnummer separert med komma'
      setError(message)
    }
  }

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const isRelatedTarget = _checkIfRelatedTarget(e)
    handleSelect(e.target, isRelatedTarget)
  }

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e

    switch (key) {
      case 'Tab': {
        const referance = ref?.current
        if (referance) referance.focus()
        break
      }

      case 'Enter':
        handleSelect({ value: term })
        break

      default:
        break
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isRelatedTarget = _checkIfRelatedTarget(e)
    if (!e.target.value.length || !isRelatedTarget) setError('')
    setTerm(e.target.value)
  }

  const handleOpenRecipientModal = () =>
    dispatch(
      handleRecipientModal({
        open: isEmail ? 'Email' : 'SMS',
        type: recipientType,
      })
    )

  const handleRemoveRecipient = (recipient: IMessageRecipient) => () => {
    const type = isEmail
      ? SEND_EMAIL_REMOVE_RECIPIENT_REQUESTED
      : SEND_SMS_REMOVE_RECIPIENT_REQUESTED

    dispatch({
      type,
      payload: recipient,
    })
  }

  return {
    ref,
    error,
    term,
    resetTerm,
    handleSelect,
    handleBlur,
    handleOnKeyDown,
    handleOpenRecipientModal,
    handleChange,
    handleRemoveRecipient,
  }
}

export default useRecipientHandle
